import { Component, inject, OnInit, signal } from "@angular/core";
import { DateRange } from "@angular/material/datepicker";
import { MatDialog } from "@angular/material/dialog";

import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import {
  AutoRedemptionEligibilityResult,
  AutoRedemptionTestStatePatch,
  AutoRedemptionTestStateResponse,
} from "@bitwarden/web-vault/app/models/types/auto-redemption-test-state.types";
import { AUTO_REDEEM_CREDITS } from "@bitwarden/web-vault/app/models/types/auto-credit-redemption.types";
import type {
  AutoRedemptionCronAllResponse,
  AutoRedemptionCronUserResponse,
} from "@bitwarden/web-vault/app/models/types/auto-credit-redemption.types";
import { AutoCreditRedemptionService } from "@bitwarden/web-vault/app/services/subscription/auto-credit-redemption.service";
import { AutoRedemptionTestStateService } from "@bitwarden/web-vault/app/services/subscription/auto-redemption-test-state.service";
import { CreditsService } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/credits.service";

import { AutoRedemptionTestStateJsonDialogComponent } from "./auto-redemption-test-state-json-dialog.component";

type FormModel = {
  tier: string;
  payment_cycle: string;
  payment_provider: string;
  active: boolean;
  auto_renew: boolean;
  date_renewal: string;
  date_end: string;
  date_start: string;
  subscription_id: string;
  clear_period_outcomes: boolean;
  target_credits: number;
};

@Component({
  selector: "app-auto-redemption-test-panel",
  templateUrl: "./auto-redemption-test-panel.component.html",
})
export class AutoRedemptionTestPanelComponent implements OnInit {
  private testStateService = inject(AutoRedemptionTestStateService);
  private autoRedemptionService = inject(AutoCreditRedemptionService);
  private creditsService = inject(CreditsService);
  private i18nService = inject(I18nService);
  private dialog = inject(MatDialog);

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly cronRunning = signal(false);
  readonly creditsLoading = signal(false);
  readonly currentCredits = signal<number | null>(null);
  readonly creditsRequired = AUTO_REDEEM_CREDITS;
  readonly errorMessage = signal<string | null>(null);
  readonly successMessage = signal<string | null>(null);
  readonly stateJson = signal("");
  readonly state = signal<AutoRedemptionTestStateResponse | null>(null);
  readonly datePickerNonce = signal(0);

  readonly tierOptions = ["free", "standard", "advance", "premium"];
  readonly cycleOptions = ["month", "year", "lifetime", "day", "week"];
  readonly providerOptions = ["ironfly", "stripe", "apple"];

  form: FormModel = this.emptyForm();

  readonly pickerDateRange = new DateRange<Date>(new Date(2000, 0, 1), new Date(2100, 11, 31));

  ngOnInit(): void {
    void this.reload();
  }

  async reload(): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);
    try {
      const data = await this.testStateService.getState();
      this.applyState(data);
      try {
        await this.refreshCreditsBalance();
      } catch {
        this.currentCredits.set(null);
      }
    } catch (error) {
      this.errorMessage.set(error instanceof Error ? error.message : String(error));
    } finally {
      this.loading.set(false);
    }
  }

  async save(): Promise<void> {
    this.saving.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);
    try {
      const patch: AutoRedemptionTestStatePatch = {
        tier: this.form.tier,
        payment_cycle: this.form.payment_cycle,
        payment_provider: this.form.payment_provider,
        active: this.form.active === true,
        auto_renew: this.form.auto_renew === true,
        subscription_id: this.form.subscription_id,
        date_renewal: this.toIsoFromYmd(this.form.date_renewal),
        date_end: this.toIsoFromYmd(this.form.date_end),
        date_start: this.toIsoFromYmd(this.form.date_start),
        clear_period_outcomes: this.form.clear_period_outcomes === true,
      };
      const data = await this.testStateService.saveState(patch);
      this.applyState(data);
      await this.autoRedemptionService.syncActiveSubscriptionFromGlossApi();
      await this.applyCreditsTargetIfNeeded();
      this.successMessage.set(this.i18nService.t("sourceExplorerAcrTestSaved"));
    } catch (error) {
      this.errorMessage.set(error instanceof Error ? error.message : String(error));
    } finally {
      this.saving.set(false);
    }
  }

  hasJson(): boolean {
    return this.stateJson().trim().length > 0;
  }

  openUserJsonModal(): void {
    if (!this.hasJson()) {
      return;
    }
    this.dialog.open(AutoRedemptionTestStateJsonDialogComponent, {
      width: "min(92vw, 56rem)",
      maxHeight: "90vh",
      data: { json: this.stateJson() },
    });
  }

  onTierSelected(value: string): void {
    this.form.tier = value;
  }

  onCycleSelected(value: string): void {
    this.form.payment_cycle = value;
  }

  onProviderSelected(value: string): void {
    this.form.payment_provider = value;
  }

  onActiveChange(checked: boolean): void {
    this.form.active = checked;
  }

  onAutoRenewChange(checked: boolean): void {
    this.form.auto_renew = checked;
  }

  onClearOutcomesChange(checked: boolean): void {
    this.form.clear_period_outcomes = checked;
  }

  onDateChange(dateStr: string, field: "date_renewal" | "date_end" | "date_start"): void {
    this.form[field] = String(dateStr ?? "").trim();
  }

  async applyCreditsOnly(): Promise<void> {
    this.saving.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);
    try {
      await this.applyCreditsTargetIfNeeded();
      this.successMessage.set(this.i18nService.t("sourceExplorerAcrTestCreditsApplied"));
    } catch (error) {
      this.errorMessage.set(error instanceof Error ? error.message : String(error));
    } finally {
      this.saving.set(false);
    }
  }

  async runCronForCurrentUser(): Promise<void> {
    await this.runCron(() => this.autoRedemptionService.runCronForCurrentUser({ silent: true }));
  }

  async runCronForAllEnrolled(): Promise<void> {
    if (
      !window.confirm(
        "Run cron for ALL enrolled users in this environment? This affects every enrolled account.",
      )
    ) {
      return;
    }

    await this.runCron(() => this.autoRedemptionService.runCronForAllEnrolled());
  }

  private async runCron(
    invoke: () => Promise<AutoRedemptionCronUserResponse | AutoRedemptionCronAllResponse>,
  ): Promise<void> {
    this.cronRunning.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);
    try {
      const result = await invoke();
      if ("enrollment_count" in result) {
        const summary = result.results
          .map(
            (r) =>
              `${r.user_id}: ${r.attempted ? "attempted" : "skipped"}${r.reason ? ` (${r.reason})` : ""}${r.outcome_status ? ` → ${r.outcome_status}` : ""}`,
          )
          .join("; ");
        this.successMessage.set(
          `${this.i18nService.t("sourceExplorerAcrTestCronDone")} — ${result.enrollment_count} enrollment(s). ${summary || "—"}`,
        );
      } else {
        const label = result.processed
          ? result.attempted
            ? (result.outcome?.status ?? result.reason ?? "attempted")
            : (result.reason ?? "not attempted")
          : (result.reason ?? "skipped");
        this.successMessage.set(`${this.i18nService.t("sourceExplorerAcrTestCronDone")}: ${label}`);
      }
    } catch (error) {
      this.errorMessage.set(error instanceof Error ? error.message : String(error));
    } finally {
      this.cronRunning.set(false);
    }
  }

  formatEligibility(eligibility: AutoRedemptionEligibilityResult): string {
    if ("reason" in eligibility) {
      return eligibility.reason;
    }
    return `eligible (${eligibility.renewal_period})`;
  }

  private applyState(data: AutoRedemptionTestStateResponse): void {
    this.state.set(data);
    this.stateJson.set(JSON.stringify(data, null, 2));
    const sub = data.active_subscription;
    if (sub) {
      Object.assign(this.form, {
        tier: sub.tier,
        payment_cycle: sub.payment_cycle,
        payment_provider: sub.payment_provider,
        active: sub.active === true,
        auto_renew: sub.auto_renew === true,
        date_renewal: this.toYmd(sub.date_renewal),
        date_end: this.toYmd(sub.date_end),
        date_start: this.toYmd(sub.date_start),
        subscription_id: sub.subscription_id,
        clear_period_outcomes: false,
      });
    } else {
      Object.assign(this.form, this.emptyForm());
    }
    this.datePickerNonce.update((n) => n + 1);
  }

  private async refreshCreditsBalance(): Promise<void> {
    this.creditsLoading.set(true);
    try {
      await this.creditsService.fetch();
      const total = this.creditsService.totalGoldCredits();
      this.currentCredits.set(total);
      this.form.target_credits = total;
    } catch (error) {
      this.currentCredits.set(null);
      throw error;
    } finally {
      this.creditsLoading.set(false);
    }
  }

  private async applyCreditsTargetIfNeeded(): Promise<void> {
    const target = Number(this.form.target_credits);
    if (!Number.isFinite(target) || target < 0) {
      throw new Error("Target credits must be a non-negative number");
    }

    const updated = await this.creditsService.setTotalCreditsTo(target);
    this.currentCredits.set(updated);
    this.form.target_credits = updated;
  }

  private emptyForm(): FormModel {
    return {
      tier: "premium",
      payment_cycle: "month",
      payment_provider: "ironfly",
      active: true,
      auto_renew: true,
      date_renewal: "",
      date_end: "",
      date_start: "",
      subscription_id: "ironfly",
      clear_period_outcomes: false,
      target_credits: 0,
    };
  }

  private toYmd(iso: string | undefined): string {
    if (!iso) {
      return "";
    }
    const d = new Date(iso);
    if (isNaN(d.getTime())) {
      return iso.split("T")[0] ?? "";
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  private toIsoFromYmd(ymd: string): string {
    const s = String(ymd ?? "").trim();
    if (!s) {
      return new Date().toISOString();
    }
    const parts = s.split("-");
    if (parts.length !== 3) {
      return new Date().toISOString();
    }
    const y = Number(parts[0]);
    const mo = Number(parts[1]) - 1;
    const day = Number(parts[2]);
    return new Date(y, mo, day, 12, 0, 0).toISOString();
  }
}
